/**
 * Objectives modal. Authors declare scenario-level learning objectives that
 * nodes and choices can be tagged with. Each objective has a stable id
 * (internal key, used in tag arrays) and a human-facing `code` (mutable,
 * e.g. "LO-1.2"). Optionally a Bloom's level.
 *
 * Operations:
 *   - Declare new (form at the bottom): code + description + optional level
 *   - Edit existing inline: code/description/level all updatable in-place
 *   - Delete with cascade through every `objectiveTags` array
 *
 * The id is *not* author-editable. Renames touch `code`; the id stays.
 * That's why deleting an objective doesn't need a rename equivalent.
 */
import { useState } from 'react';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import type { BloomLevel, ObjectiveDecl } from '@branchworks/shared';
import { useScenarioStore } from '../store/scenarioStore';
import { Modal } from './Modal';
import { objectiveColor } from '../panel/ObjectivePalette';

const BLOOM_LEVELS: BloomLevel[] = [
  'remember', 'understand', 'apply', 'analyze', 'evaluate', 'create',
];

export function ObjectivesModal() {
  const scenario = useScenarioStore((s) => s.scenario);
  const setOpenModal = useScenarioStore((s) => s.setOpenModal);
  const addObjective = useScenarioStore((s) => s.addObjective);
  const updateObjective = useScenarioStore((s) => s.updateObjective);
  const deleteObjective = useScenarioStore((s) => s.deleteObjective);

  const objectives = scenario.objectives || {};
  const entries = Object.entries(objectives);

  const [draftCode, setDraftCode] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftLevel, setDraftLevel] = useState<BloomLevel | ''>('');
  const [addError, setAddError] = useState('');

  const tryAdd = () => {
    const result = addObjective({
      code: draftCode,
      description: draftDescription,
      ...(draftLevel ? { bloomLevel: draftLevel } : {}),
    });
    if (!result.ok) {
      setAddError(result.error);
      return;
    }
    setDraftCode('');
    setDraftDescription('');
    setDraftLevel('');
    setAddError('');
  };

  return (
    <Modal
      kicker="Scenario configuration"
      title="Learning objectives"
      onClose={() => setOpenModal(null)}
      maxWidth={720}
      footer={
        <div>
          <div className="text-[10px] uppercase tracking-widest mb-2 text-ink-2">
            Declare new objective
          </div>
          <div className="grid grid-cols-[120px_1fr_160px_auto] gap-2 items-end">
            <input
              value={draftCode}
              onChange={(e) => {
                setDraftCode(e.target.value);
                setAddError('');
              }}
              placeholder="LO-N"
              className="px-3 py-2 rounded border border-cream-border bg-white text-sm font-mono"
            />
            <input
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              placeholder="Description (what the objective measures)"
              className="px-3 py-2 rounded border border-cream-border bg-white text-sm"
            />
            <select
              value={draftLevel}
              onChange={(e) => setDraftLevel(e.target.value as BloomLevel | '')}
              className="px-3 py-2 rounded border border-cream-border bg-white text-sm"
            >
              <option value="">— Bloom level —</option>
              {BLOOM_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
            <button
              onClick={tryAdd}
              className="px-4 py-2 rounded text-sm flex items-center gap-1.5 bg-ink text-cream-2"
            >
              <Plus size={14} /> Add
            </button>
          </div>
          {addError && (
            <div className="mt-2 text-xs flex items-center gap-1.5 text-terracotta-dark">
              <AlertCircle size={12} /> {addError}
            </div>
          )}
        </div>
      }
    >
      {entries.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3">
          {entries.map(([id, decl]) => (
            <ObjectiveRow
              key={id}
              id={id}
              decl={decl}
              objectivesMap={objectives}
              onUpdate={(patch) => updateObjective(id, patch)}
              onDelete={() => {
                if (
                  confirm(
                    `Delete "${decl.code}"? Every node and choice tagged with it will lose the tag.`,
                  )
                ) {
                  deleteObjective(id);
                }
              }}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

function EmptyState() {
  return (
    <div className="text-sm italic text-center py-8 px-4 rounded border border-dashed border-cream-border text-ink-2">
      <div className="font-bold not-italic mb-1 text-ink">No objectives yet.</div>
      Declare learning objectives so nodes and choices can be tagged with what they
      practice or assess. Tags appear as colored dots on canvas cards and pills in
      the detail panel.
    </div>
  );
}

interface ObjectiveRowProps {
  id: string;
  decl: ObjectiveDecl;
  objectivesMap: Record<string, ObjectiveDecl>;
  onUpdate: (patch: Partial<ObjectiveDecl>) => void;
  onDelete: () => void;
}

function ObjectiveRow({
  id, decl, objectivesMap, onUpdate, onDelete,
}: ObjectiveRowProps) {
  const color = objectiveColor(objectivesMap, id);

  return (
    <div className="rounded border p-3 border-cream-border bg-cream-2">
      <div className="flex items-start gap-2">
        <span
          className="mt-2 w-3 h-3 rounded-full flex-shrink-0"
          style={{ background: color }}
          title={`Palette color for ${decl.code}`}
        />
        <div className="flex-1 grid grid-cols-[120px_1fr_160px] gap-2">
          <div>
            <label className="text-[9px] uppercase tracking-widest block mb-1 text-ink-2">
              Code
            </label>
            <input
              value={decl.code}
              onChange={(e) => onUpdate({ code: e.target.value })}
              className="w-full px-2 py-1.5 rounded border border-cream-border bg-white text-sm font-mono"
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest block mb-1 text-ink-2">
              Description
            </label>
            <input
              value={decl.description}
              onChange={(e) => onUpdate({ description: e.target.value })}
              className="w-full px-2 py-1.5 rounded border border-cream-border bg-white text-sm"
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest block mb-1 text-ink-2">
              Bloom level
            </label>
            <select
              value={decl.bloomLevel || ''}
              onChange={(e) =>
                onUpdate({
                  bloomLevel: (e.target.value || undefined) as BloomLevel | undefined,
                })
              }
              className="w-full px-2 py-1.5 rounded border border-cream-border bg-white text-sm"
            >
              <option value="">—</option>
              {BLOOM_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="mt-6 text-xs hover:underline self-start flex items-center gap-1 text-terracotta-dark"
          title="Delete this objective. Every node and choice tagged with it will lose the tag."
        >
          <Trash2 size={12} /> Remove
        </button>
      </div>
    </div>
  );
}
